import type { ComponentType } from "react";
import type {
  ArtifactSectionPart,
  ArtifactSectionTemplate,
} from "../../components/registry/artifactSectionTemplates";
import { artifactSectionTemplates } from "../../components/registry/artifactSectionTemplates";
import type { StoryWidget } from "../dataStories/generateStory.types";

/**
 * StoryWidget[] → Sections panel view-model.
 *
 * Pure and stateless — no visibility/checked state, no callbacks. The panel's
 * checked state lives in `useArtifactVisibility`; this file only answers
 * "what rows and groups exist right now", per §9 (data shaping stays out of
 * JSX and out of the hook).
 */

export interface ArtifactSectionPartVm extends ArtifactSectionPart {}

export interface ArtifactSectionRowVm {
  /** The underlying canvas item id — also the key used by useArtifactVisibility. */
  id: string;
  label: string;
  icon: ComponentType<{ size?: number | string }>;
  /** Sub-elements to render as indented child rows; [] renders a flat row. */
  parts: readonly ArtifactSectionPartVm[];
}

export interface ArtifactSectionGroupVm {
  /** Stable kebab-case id derived from the group label (static config, not user data). */
  id: string;
  label: string;
  rows: readonly ArtifactSectionRowVm[];
}

const toGroupId = (groupLabel: string): string =>
  groupLabel
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

/**
 * A widget's row label. Branches on shape (not on rendering) because the
 * underlying widget interfaces genuinely differ — same category of derivation
 * as `resolveChartAiSummarySections`. Falls back to a type-specific default
 * name when the backend hasn't sent a title yet (several content types mark
 * `title` "not yet sent by the backend" in generateStory.types.ts).
 */
function resolveRowLabel(widget: StoryWidget): string {
  switch (widget.type) {
    case "kpi":
      return widget.name;
    case "chart":
      return widget.title;
    case "insight":
      return widget.title || "AI insights";
    case "summary":
      return widget.title || "AI summary";
    case "narrative":
      return widget.title || "AI narrative";
    case "report":
    case "ingested_widget":
    case "captured_widget":
      return widget.title;
    case "link":
      return widget.title || "Link";
    case "embed":
      return widget.title || "Embed";
    case "image":
      return widget.title || "Image";
    case "text":
      return widget.title || "Text box";
    default:
      return (widget as StoryWidget).id;
  }
}

const matchesQuery = (row: ArtifactSectionRowVm, query: string): boolean => {
  if (!query) return true;
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  if (row.label.toLowerCase().includes(needle)) return true;
  return row.parts.some((part) => part.label.toLowerCase().includes(needle));
};

/**
 * Maps the story's live widgets onto the Sections panel's groups, in registry
 * order, skipping any group with zero matching rows (a widget type the story
 * doesn't use never renders an empty group — decision confirmed with user).
 */
export function mapArtifactSections(
  widgets: readonly StoryWidget[],
  options: {
    templates?: readonly ArtifactSectionTemplate[];
    query?: string;
  } = {},
): ArtifactSectionGroupVm[] {
  const templates = options.templates ?? artifactSectionTemplates;
  const query = options.query ?? "";

  const templateByType = new Map(templates.map((t) => [t.type, t]));

  const rowsByGroupLabel = new Map<string, ArtifactSectionRowVm[]>();

  widgets.forEach((widget) => {
    const template = templateByType.get(widget.type);
    // A widget type the registry hasn't caught up to yet — skip rather than
    // throw; the group simply won't show this row until it's registered.
    if (!template) return;

    const row: ArtifactSectionRowVm = {
      id: widget.id,
      label: resolveRowLabel(widget),
      icon: template.icon,
      parts: template.parts,
    };

    if (!matchesQuery(row, query)) return;

    const existing = rowsByGroupLabel.get(template.groupLabel);
    if (existing) {
      existing.push(row);
    } else {
      rowsByGroupLabel.set(template.groupLabel, [row]);
    }
  });

  // Registry order decides group order; only registry group labels that
  // picked up at least one row are ever emitted, in registry-first-seen order.
  const orderedGroupLabels: string[] = [];
  templates.forEach((t) => {
    if (rowsByGroupLabel.has(t.groupLabel) && !orderedGroupLabels.includes(t.groupLabel)) {
      orderedGroupLabels.push(t.groupLabel);
    }
  });

  return orderedGroupLabels.map((label) => ({
    id: toGroupId(label),
    label,
    rows: rowsByGroupLabel.get(label) ?? [],
  }));
}

/**
 * Drops any widget whose id is in `hiddenIds` — the Sections panel's
 * whole-row toggle. Pure so `useArtifactVisibility` and its callers stay
 * testable without React; unrelated to `mapArtifactSections`, which only
 * *lists* rows and never decides what an artifact renders.
 */
export function filterVisibleWidgets(
  widgets: readonly StoryWidget[],
  hiddenIds: ReadonlySet<string>,
): StoryWidget[] {
  if (hiddenIds.size === 0) return widgets as StoryWidget[];
  return widgets.filter((widget) => !hiddenIds.has(widget.id));
}
